/**
 * SubjectConfirmPrompt.tsx
 *
 * Shown after 1–2 sessions when the system has inferred a subject
 * from the user's topic input. Low-friction: one tap to confirm.
 *
 * "We've been tailoring this for Biology — is that right?"
 */
import React, { useEffect } from "react";
import { Check, ChevronRight } from "lucide-react";
import { ContextualPrompt } from "./ContextualPrompt";
import {
  useProgressiveProfile,
  describeInference,
} from "../../context/ProgressiveProfileContext";

// Compact list of subjects for quick overrides
const QUICK_SUBJECTS = [
  "Mathematics", "Physics", "Chemistry", "Biology",
  "English Language", "History", "Economics", "Geography",
  "Literature in English", "Government", "Accounting",
  "ICT / Computer Science",
];

export const SubjectConfirmPrompt: React.FC = () => {
  const { state, shouldShowPrompt, markShown, confirmSubject, skipPrompt } =
    useProgressiveProfile();

  const visible = shouldShowPrompt("subjectConfirm");
  const inferred = state.data.inferredSubject;

  useEffect(() => {
    if (visible && state.prompts.subjectConfirm !== "shown") {
      markShown("subjectConfirm");
    }
  }, [visible]);

  const [showOverride, setShowOverride] = React.useState(false);

  if (!inferred) return null;

  const handleConfirm = () => confirmSubject(true);
  const handleOverride = (subject: string) => confirmSubject(false, subject);
  const handleDismiss = () => skipPrompt("subjectConfirm");

  return (
    <ContextualPrompt
      show={visible}
      title={`Studying ${inferred}?`}
      subtitle="We've been tailoring your experience around this subject."
      onDismiss={handleDismiss}
      footerNote="Takes 1 tap · Improves all recommendations"
    >
      {!showOverride ? (
        <div className="flex gap-2 pb-3 mt-1">
          {/* Confirm button */}
          <button
            onClick={handleConfirm}
            className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-[var(--accent-primary)] text-[var(--accent-on-primary)] font-bold text-sm transition-all active:scale-[0.97]"
          >
            <Check className="w-4 h-4" />
            Yes, {inferred}
          </button>

          {/* Change button */}
          <button
            onClick={() => setShowOverride(true)}
            className="flex items-center justify-center gap-1.5 h-11 px-4 rounded-xl border border-line text-[var(--text-muted)] hover:border-line-strong hover:text-[var(--text-primary)] font-medium text-sm transition-all"
          >
            Change
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="pb-3 mt-1">
          <p className="text-[11px] text-[var(--text-muted)] font-bold uppercase tracking-widest mb-2">
            Select your main subject
          </p>
          <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
            {QUICK_SUBJECTS.map((s) => (
              <button
                key={s}
                onClick={() => handleOverride(s)}
                className="px-3 py-1.5 rounded-full border border-line text-[12px] text-[var(--text-muted)] hover:border-tok-accent-primary/60 hover:text-[var(--text-primary)] transition-all"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </ContextualPrompt>
  );
};

/**
 * GoalPrompt.tsx
 *
 * Shown after the user's first completed session.
 * "Nice work  What are you working towards?"
 */
export const GoalPrompt: React.FC = () => {
  const { state, shouldShowPrompt, markShown, completeGoal, skipPrompt } =
    useProgressiveProfile();

  const visible = shouldShowPrompt("goals");

  useEffect(() => {
    if (visible && state.prompts.goals !== "shown") {
      markShown("goals");
    }
  }, [visible]);

  const GOALS = [
    { id: "pass-waec", label: "Pass WAEC / BECE" },
    { id: "improve-grades", label: "Improve my grades" },
    { id: "exam-prep", label: "Prepare for exams" },
    { id: "learn-faster", label: "Learn faster" },
    { id: "general", label: "General studying" },
  ];

  return (
    <ContextualPrompt
      show={visible}
      title="Nice work! What are you working towards?"
      subtitle="We'll personalize your experience around your goal."
      onDismiss={() => skipPrompt("goals")}
      footerNote="Takes 5 seconds · Always skippable"
    >
      <div className="grid grid-cols-1 gap-2 pb-3 mt-1">
        {GOALS.map((g) => (
          <button
            key={g.id}
            onClick={() => completeGoal(g.id)}
            className="flex items-center gap-3 h-11 px-4 rounded-xl border border-line text-left text-sm text-[var(--text-muted)] hover:border-tok-accent-primary/50 hover:text-[var(--text-primary)] hover:bg-tok-accent-primary/5 transition-all active:scale-[0.98] font-medium"
          >
            {g.label}
          </button>
        ))}
      </div>
    </ContextualPrompt>
  );
};

/**
 * SubjectPrompt.tsx
 *
 * Shown if no subjects are set and no inferred subject to confirm.
 * "Want better results? Tell us what you're studying."
 */
export const SubjectPrompt: React.FC = () => {
  const { state, shouldShowPrompt, markShown, completeSubjects, skipPrompt } =
    useProgressiveProfile();

  const visible = shouldShowPrompt("subjects");
  const [selected, setSelected] = React.useState<string[]>([]);

  useEffect(() => {
    if (visible && state.prompts.subjects !== "shown") {
      markShown("subjects");
    }
  }, [visible]);

  const SUBJECTS = [
    "Mathematics", "English Language", "Physics", "Chemistry",
    "Biology", "History", "Economics", "Geography",
    "Government", "Literature in English", "Accounting", "ICT",
  ];

  const toggle = (s: string) =>
    setSelected((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  return (
    <ContextualPrompt
      show={visible}
      title="What subjects are you studying?"
      subtitle="Helps us give you more relevant quizzes and materials."
      onDismiss={() => skipPrompt("subjects")}
      footerNote="Select as many as you like"
    >
      <div className="flex flex-wrap gap-2 pb-2 mt-1">
        {SUBJECTS.map((s) => {
          const on = selected.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggle(s)}
              className={`px-3 py-1.5 rounded-full border text-[12px] font-medium transition-all ${
                on
                  ? "border-[var(--accent-primary)] bg-tok-accent-primary/10 text-[var(--text-primary)]"
                  : "border-line text-[var(--text-muted)] hover:border-line-strong hover:text-[var(--text-primary)]"
              }`}
            >
              {s}
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <button
          onClick={() => completeSubjects(selected)}
          className="w-full mt-3 mb-2 h-11 rounded-xl bg-[var(--accent-primary)] text-[var(--accent-on-primary)] font-bold text-sm transition-all active:scale-[0.97]"
        >
          Save {selected.length} subject{selected.length !== 1 ? "s" : ""}
        </button>
      )}
    </ContextualPrompt>
  );
};

/**
 * ProgressivePromptOrchestrator
 *
 * Mount this once in the app shell. It decides which prompt to show,
 * ensuring only ONE is shown per session at a time.
 */
export const ProgressivePromptOrchestrator: React.FC = () => {
  const { shouldShowPrompt } = useProgressiveProfile();

  // Priority order: subject confirm > goal > subjects
  if (shouldShowPrompt("subjectConfirm")) return <SubjectConfirmPrompt />;
  if (shouldShowPrompt("goals")) return <GoalPrompt />;
  if (shouldShowPrompt("subjects")) return <SubjectPrompt />;

  return null;
};
